import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  Divider,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  AddCircleOutlineRounded,
  AddCircleRounded,
  AddToDrive,
  ArrowBackIosRounded,
  ArrowDropDown,
  ArrowForwardIosRounded,
  AttachFile,
  Label,
  LabelOutlined,
  UploadFile,
} from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import {
  useAttachments,
  useBreakpoint,
  useSelectedOrganization,
  useSnackbar,
  useTranslations,
  useUser,
} from "hooks";

import { Comment, History } from '@mui/icons-material';

import DialogHeader from "./DialogHeader";
import { getTranslation } from "common";
import CommentsSection, {
  Comment as TSXComment,
  CompleteComment,
} from "components/CommentsSection";
import { flownodesApi, organizationsApi, processesApi, statisticsApi } from "api";
import {
  getLabelDataFromJsonTags,
  getUserData,
  toBase64,
} from "common/utilities";
import AttachmentList from "components/AttachmentList";
import { TaskCardButtonDialog } from "./TaskCardButtonsDialog";
import { Priority } from "hooks/usePriorities";
import { SequenceFlow, Task } from "model/activitiesModel";
import LabelManager from "../labels/LabelManager";
import FieldsList from "components/FieldsList";
import Labels from "components/labels/Labels";
import useDrivePicker from "react-google-drive-picker";
import useAppservices from "hooks/useAppservices";
import { useGoogleLogin } from "@react-oauth/google";
import axios, { AxiosError } from "axios";
import { UserData } from "hooks/useUserDataKeys";
import useSubscriptionFeatures from "hooks/useSubscriptionFeatures";
import { useNavigate } from "react-router-dom";
import HistoriesSection from "components/HistoriesSection";
import GoogleDriveMenu from "components/menus/GoogleDriveMenu";
import ProcessNavigationMenu from "components/menus/ProcessNavigationMenu";

type SupportedTypes =
  | "document"
  | "spreadsheets"
  | "presentation"
  | "drawing"
  | "folder";

interface BaseTaskDetailDialogProps {
  open: boolean;
  doing: boolean;
  onClose: () => void;
  nextButtonLabel?: string;
  undoButtonLabel?: string;
  handleNextStep?: (task: Task) => void;
  handleBackStep?: (task: Task) => void;
  handleSequenceFlowChoice?: (task: Task, sequenceFlow: SequenceFlow) => void;
  administrator?: boolean;
  task?: Task;
  readonly?: boolean;
  onPriorityUpdate?: (taskId: number, newPriority: Priority) => void;
  refresh?: () => void;
}

const TaskDetailDialog: React.FC<BaseTaskDetailDialogProps> = ({
  open,
  doing,
  onClose,
  task,
  readonly = false,
  handleSequenceFlowChoice,
  handleNextStep,
  handleBackStep,
  nextButtonLabel,
  undoButtonLabel,
  administrator,
  onPriorityUpdate,
  refresh,
}) => {
  // hooks & state
  const user = useUser();
  const [openPicker, authResponse] = useDrivePicker();
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuPhase, setMenuPhase] = useState<"main" | "create">("main");
  const redoGoogleDoc = useRef<SupportedTypes | null>(null);
  const { appservices, handleLinkAppservice } = useAppservices();
  const {
    storageDiskCanUse,
    bypassSubscription,
    checkLoading,
    refreshSubscriptionFeatures,
    storageUsage,
  } = useSubscriptionFeatures();
  const navigate = useNavigate();
  const menuOpen = Boolean(anchorEl);

  // snackbar, translations, organization
  const { sendSnack } = useSnackbar();
  const translations = useTranslations();
  const isMobile = useBreakpoint("down", "sm");
  const selectedOrganization = useSelectedOrganization();
  let organizationId: number | null | undefined = null;
  if (selectedOrganization) {
    organizationId = selectedOrganization.id;
  }

  // data state
  const [comments, setComments] = useState<CompleteComment[]>([]);
  const [history, setHistory] = useState<any[]>([]);
  const [attachments, , getAttachments, images, histories, loadingAttachments] = useAttachments(task || null);
  const [uploadAttachmentError, setUploadAttachmentError] = useState(false);
  const [maxAttachmentsError, setMaxAttachmentsError] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [allPriorities, setAllPriorities] = useState<Priority[]>([]);
  const [selectedPriority, setSelectedPriority] = useState<Priority | null>(null);
  const [isLabelManagerShown, setIsLabelManagerShown] = useState<HTMLElement | null>(null);
  const [userdataEntries, setUserDataEntries] = useState<{ [key: string]: UserData }>({});
  const [openAddFieldDialog, setOpenAddFieldDialog] = useState(false);
  const [activeView, setActiveView] = useState<'histories' | 'comments'>('comments');
  // helper toggles
  const toggleShowMore = () => setShowMore((c) => !c);
  const handleLabelManagerClose = () => setIsLabelManagerShown(null);

  // Menu handlers
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setMenuPhase("main");
  };
  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    setMenuPhase("main");
    setIsPickerOpen(false);
    document.querySelectorAll(".picker").forEach((el) => el.remove());
  }, []);
  const handleOpenCreateMenu = () => setMenuPhase("create");
  const handleBackToMainMenu = () => setMenuPhase("main");

  // Drive picker helpers
  const handleOpenPicker = () => {
    setIsPickerOpen(true);
    openPicker({
      locale: user?.i18n,
      clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID2 || "",
      developerKey: process.env.REACT_APP_GOOGLE_DEVELOPER_KEY || "",
      viewId: "DOCS",
      token: authResponse?.access_token,
      showUploadView: true,
      supportDrives: true,
      multiselect: true,
      callbackFunction: async (data: any) => {
        if (data.action === "picked") {
          const files = data.docs;
          let allFiles = files.map(async (file: any) => {
            const payload = {
              idorganization: organizationId,
              filename: file.name,
              cloudlink: `https://drive.google.com/file/d/${file.id}/view`,
              idtype: getIdTypeFromMimeType(file.mimeType),
              description: file.name,
            };
            return flownodesApi.post(`${task?.idprocesstoken}/attachments`, payload);
          });
          await Promise.all(allFiles);
          sendSnack({
            type: "success",
            message: getTranslation(translations, "generic.fileuploadedsuccessfully"),
          });
          getAttachments();
        }
        if (data.action === "cancel" || data.action === "picked") {
          setIsPickerOpen(false);
          handleMenuClose();
        }
      },
    });
  };

  const handleFolderPicker = () => {
    setIsPickerOpen(true);
    openPicker({
      locale: user?.i18n,
      clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID2 || "",
      developerKey: process.env.REACT_APP_GOOGLE_DEVELOPER_KEY || "",
      viewId: "FOLDERS",
      token: authResponse?.access_token,
      showUploadFolders: true,
      supportDrives: true,
      multiselect: true,
      setSelectFolderEnabled: true,
      setIncludeFolders: true,
      callbackFunction: async (data: any) => {
        if (data.action === "picked") {
          const folders = data.docs.filter((doc: any) => doc.mimeType === "application/vnd.google-apps.folder");
          let allFolders = folders.map(async (folder: any) => {
            const payload = {
              idorganization: organizationId,
              filename: folder.name,
              cloudlink: `https://drive.google.com/drive/folders/${folder.id}`,
              description: folder.name || "",
              idtype: 21,
            };
            return flownodesApi.post(`${task?.idprocesstoken}/attachments`, payload);
          });
          await Promise.all(allFolders);
          sendSnack({
            type: "success",
            message: getTranslation(translations, "generic.folderuploadedsuccessfully"),
          });
          getAttachments();
        }
        if (data.action === "cancel" || data.action === "picked") {
          setIsPickerOpen(false);
          handleMenuClose();
        }
      },
    });
  };

  useEffect(() => {
    const setPickerStyles = () => {
      const pickers = document.querySelectorAll(".picker-dialog") as NodeListOf<HTMLElement>;
      if (pickers.length > 0) {
        const currentPicker = pickers[pickers.length - 1];
        currentPicker.style.zIndex = "99999";
      }
    };
    if (authResponse && isPickerOpen) {
      setTimeout(() => {
        setPickerStyles();
      }, 50);
    }
  }, [isPickerOpen, authResponse]);

  const getIdTypeFromMimeType = (mimeType: string) => {
    switch (mimeType) {
      case "application/vnd.google-apps.folder":
        return 21;
      case "application/vnd.google-apps.document":
        return 22;
      case "application/vnd.google-apps.spreadsheet":
        return 23;
      case "application/vnd.google-apps.presentation":
        return 24;
      case "application/vnd.google-apps.drawing":
        return 25;
      default:
        return 20;
    }
  };

  const failureGoogle: () => void = () => {
    console.error("fail");
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async (e: any) => {
      const data = await handleLinkAppservice(e, 1);
      const token = data?.token;
      if (redoGoogleDoc.current && token) {
        await createAndUploadGoogleDoc(redoGoogleDoc.current, token);
      }
      redoGoogleDoc.current = null;
    },
    onError: failureGoogle,
    flow: "auth-code",
    enable_serial_consent: false,
    scope:
      "https://www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/drive.readonly https://www.googleapis.com/auth/presentations",
  });

  const createGoogleDoc = async (type: string, accessToken: string) => {
    try {
      let mimeType = "";
      switch (type) {
        case "folder":
          mimeType = "application/vnd.google-apps.folder";
          break;
        case "spreadsheets":
          mimeType = "application/vnd.google-apps.spreadsheet";
          break;
        case "presentation":
          mimeType = "application/vnd.google-apps.presentation";
          break;
        case "document":
          mimeType = "application/vnd.google-apps.document";
          break;
        case "drawing":
          mimeType = "application/vnd.google-apps.drawing";
          break;
        default:
          throw new Error(getTranslation(translations, "google.file.not.supported"));
      }
      try {
        const response = await axios.post(
          "https://content.googleapis.com/drive/v3/files?supportsTeamDrives=true&fields=webViewLink%2Cname&alt=json",
          {
            mimeType,
            name: task?.flownodename,
          },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );
        return { docData: response.data, mimeType };
      } catch (error: any) {
        const _error: AxiosError = { ...error };
        if (_error.response?.status && _error.response?.status === 401) {
          redoGoogleDoc.current = type as SupportedTypes;
          googleLogin();
        }
      }
    } catch (error) {
      // ignore
    }
    return { docData: null, mimeType: null };
  };

  const createAndUploadGoogleDoc = async (type: SupportedTypes, access_token: string | null = null) => {
    if (!appservices[1]) {
      sendSnack({
        type: "error",
        message: getTranslation(translations, "google.auth.failed.for.doc"),
      });
      return;
    }
    try {
      const { docData, mimeType } = await createGoogleDoc(type, access_token || appservices[1]);
      if (!docData) {
        sendSnack({
          type: "error",
          message: getTranslation(translations, "google.doc.create.error"),
        });
        return;
      }
      const payload = {
        idorganization: organizationId,
        filename: docData.name,
        cloudlink: docData.webViewLink,
        idtype: getIdTypeFromMimeType(mimeType || ""),
        description: docData.name,
      };
      await flownodesApi.post(`${task?.idprocesstoken}/attachments`, payload);
      sendSnack({
        type: "success",
        message: getTranslation(translations, "generic.fileuploadedsuccessfully"),
      });
      getAttachments();
    } catch {
      sendSnack({
        type: "error",
        message: getTranslation(translations, "generic.erroroccured"),
      });
    }
  };

  // fetch comments/history
  const getComments = useCallback(async () => {
    if (!task) return;
    try {
      const response = await processesApi.get(`${task.idprocess}/comments`);
      setComments([...response.data.comments]);
    } catch {
      // ignore
    }
  }, [task]);

  const getHistory = useCallback(async () => {
    if (!task) return;
    try {
      const response = await statisticsApi.get(`/process/${task.idprocesstoken}/tasks/history`);
      setHistory(response.data.data);
    } catch {
      // ignore
    }
  }, [task]);

  const onComment = async ({ comment }: TSXComment) => {
    if (task && comment) {
      try {
        await flownodesApi.post(`${task.idprocesstoken}/comments`, { comment });
        getComments();
      } catch {
        // ignore
      }
    }
  };

  // upload local files
  const handleUploadFile = async (files: FileList | null) => {
    if (!task) return;
    let promises: Promise<any>[] = [];
    let hasError = false;
    try {
      if (files && files.length > 0) {
        const newFiles = Array.from(files);
        const fileSize = newFiles.reduce((acc, file) => acc + file.size, 0);
        const totalSize = (storageUsage?.using || 0) + fileSize;
        if (totalSize > (storageUsage?.permit || 0) && !bypassSubscription) {
          setUploadAttachmentError(true);
          return;
        }
        const totalAttachmentCount = attachments.length + newFiles.length;
        if (totalAttachmentCount > 10) {
          setMaxAttachmentsError(true);
          return;
        }
        for (let file of newFiles) {
          const payload = {
            idorganization: organizationId,
            filename: file.name,
            cloudlink: "",
            idtype: 1,
            description: file.name,
            blob: await toBase64(file),
            size: file.size,
          };
          promises.push(flownodesApi.post(`${task.idprocesstoken}/attachments`, payload));
        }
        try {
          await Promise.all(promises);
        } catch {
          hasError = true;
        }
        if (!hasError) {
          sendSnack({
            type: "success",
            message: getTranslation(translations, "generic.fileuploadedsuccessfully"),
          });
        }
        refreshSubscriptionFeatures();
        getAttachments();
      }
      setUploadAttachmentError(false);
      setMaxAttachmentsError(false);
    } finally {
      // nothing
    }
  };

  // priorities
  const fetchOrganizationPriorities = useCallback(async () => {
    if (!organizationId) return;
    try {
      const response = await organizationsApi.get(`/${organizationId}/priorities`);
      if (response && response.data && response.data.priorities) {
        setAllPriorities(response.data.priorities);
      }
    } catch {
      // ignore
    }
  }, [organizationId]);

  useEffect(() => {
    if (open) {
      fetchOrganizationPriorities();
    }
  }, [fetchOrganizationPriorities, open]);

  useEffect(() => {
    if (task && task.priority_name) {
      const priority = allPriorities.find((p) => p.name === task.priority_name);
      setSelectedPriority(priority || null);
    }
  }, [allPriorities, task]);

  const updatePriority = async (newPriority: Priority) => {
    if (!task) return;
    try {
      const { data } = await flownodesApi.put(`/${task.idprocess}/priorities/${newPriority.id}`, {});
      setSelectedPriority(newPriority);
      onPriorityUpdate && onPriorityUpdate(task.idprocesstoken, newPriority);
      sendSnack({ type: "success", message: data.responsemessage });
    } catch {
      // ignore
    }
  };

  const handlePriorityChange = (event: SelectChangeEvent<string | number>) => {
    const newPriority = allPriorities.find((priority) => priority.id === Number(event.target.value));
    if (newPriority) {
      updatePriority(newPriority);
    }
  };

  // userdata parsing
  useEffect(() => {
    const parsedUserData = getUserData(task?.userdata);
    const userDataEntries = Object.fromEntries(
      Object.entries(parsedUserData).map(([key, item]) => [key, item])
    );
    setUserDataEntries(userDataEntries);
    Object.entries(parsedUserData).forEach(([key, value]) => {
      if ((value as UserData).inCard) {
        localStorage.setItem(`userdata-per-${task?.idprocess}`, key);
      }
    });
  }, [task]);

  useEffect(() => {
    if (open) {
      getComments();
      getAttachments();
      getHistory();
    }
  }, [getComments, getAttachments, open]);

  // RENDER
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="lg"
      disableEscapeKeyDown={true}
      PaperProps={{
        sx: {
          borderRadius: 2,
          height: "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box sx={{ bgcolor: "#f4f5f7" }}>

        <DialogHeader onClose={onClose}>
          <Box sx={{ px: 2, width: "100%", }}>
            {task && (
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box flex={1}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {task.flownodename || task.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {task.processmodelname}
                  </Typography>
                </Box>
                <Labels labelCompleteMode labels={getLabelDataFromJsonTags(task?.jsontag)} />
              </Stack>
            )}
          </Box>
        </DialogHeader>

        {/* Action buttons bar */}
        <Box
          sx={{
            px: 3,
            py: 1.5,
            bgcolor: "background.paper",
            borderBottom: "1px solid rgba(0,0,0,0.1)",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {/* Priority */}
            {selectedPriority && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {getTranslation(translations, "generic.priorities.caption")}:
                </Typography>
                {doing && !readonly ? (
                  <Select
                    value={selectedPriority.id}
                    onChange={handlePriorityChange}
                    displayEmpty
                    size="small"
                    sx={{
                      minWidth: 100,
                      height: 32,
                      "& .MuiSelect-select": { py: 0.5, px: 1 },
                    }}
                  >
                    {allPriorities.map((priority) => (
                      <MenuItem key={priority.id} value={priority.id}>
                        {priority.name}
                      </MenuItem>
                    ))}
                  </Select>
                ) : (
                  <Chip
                    label={selectedPriority.name}
                    color="primary"
                    size="small"
                  />
                )}
              </Box>
            )}

            {/* Action buttons - visible only when doing and not readonly */}
            {!readonly && doing && (
              <>

                <Button
                  startIcon={<LabelOutlined />}
                  size="small"
                  variant="outlined"
                  onClick={(e) => setIsLabelManagerShown(e.currentTarget)}
                >
                  {getTranslation(translations, "generic.labels.caption")}
                </Button>

                 <Button
                      id="addfield-button"
                      startIcon={<AddCircleOutlineRounded />}
                      size="small"
                      variant="outlined"
                      onClick={() => setOpenAddFieldDialog(true)}
                    >
                      {getTranslation(translations, "pm.mayactivities.addfield.label")}
                    </Button>


                {checkLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <>
                    <Button
                      id="upload-attachment-button-3"
                      startIcon={<AttachFile />}
                      size="small"
                      variant="outlined"
                      component="label"
                      disabled={!storageDiskCanUse && !bypassSubscription}
                    >
                      {getTranslation(translations, "settings.attachments.label")}
                      <input
                        id="upload-attachment-button-2"
                        type="file"
                        multiple
                        hidden
                        onChange={({ target }) => handleUploadFile(target.files)}
                      />
                    </Button>

                    <Button
                      id="basic-button"
                      aria-controls={menuOpen ? "basic-menu" : undefined}
                      aria-haspopup="true"
                      aria-expanded={menuOpen ? "true" : undefined}
                      onClick={handleClick}
                      size="small"
                      variant="outlined"
                      startIcon={<AddToDrive />}
                    >
                      {getTranslation(translations, "pm.startprocess.button.uploadfile.drive")}
                    </Button>

                    <Button
                      id="addfield-button"
                      startIcon={<AddCircleOutlineRounded />}
                      size="small"
                      variant="outlined"
                      onClick={() => setOpenAddFieldDialog(true)}
                    >
                      {getTranslation(translations, "pm.mayactivities.addfield.label")}
                    </Button>


                  </>
                )}
              </>
            )}
            <ProcessNavigationMenu
              task={task}
              handleNextStep={handleNextStep}
              handleBackStep={handleBackStep}
              handleSequenceFlowChoice={handleSequenceFlowChoice}
              nextButtonLabel={nextButtonLabel}
              undoButtonLabel={undoButtonLabel}
              onClose={onClose}
            />

            <Box sx={{ flex: 1 }} />

            {/* History switch - always visible */}

            <ToggleButtonGroup
              value={activeView}
              exclusive
              onChange={(event, newView) => {
                if (newView !== null) {
                  setActiveView(newView);
                  setShowHistory(newView === 'histories');
                }
              }}
              size="small"
              aria-label="view selection"
            >
              <Tooltip title={getTranslation(translations, "settings.comments.label")}>
                <ToggleButton
                  value="comments"
                  aria-label="comments"
                >
                  <Comment />
                </ToggleButton>

              </Tooltip>
              <Tooltip title={getTranslation(translations, "settings.histories.label")}>
                <ToggleButton value="histories" aria-label="histories">
                  <History />
                </ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>
          </Stack>

          {/* Error alerts */}
          {!readonly && doing && (
            <>
              {((!storageDiskCanUse && !bypassSubscription) || uploadAttachmentError) && (
                <Alert
                  sx={{ mt: 1, py: 0.5 }}
                  severity="error"
                  action={
                    <Button
                      size="small"
                      onClick={() => navigate(`/dashboard/organizations/${organizationId}/billing`, {
                        state: { organizationName: selectedOrganization?.name }
                      })}
                    >
                      {getTranslation(translations, "generic.upgrade")}
                    </Button>
                  }
                >
                  {getTranslation(translations, "storage.limit.reached")}
                </Alert>
              )}

              {maxAttachmentsError && (
                <Alert
                  id="max-attachments-error"
                  sx={{ mt: 1, py: 0.5 }}
                  severity="error"
                >
                  {getTranslation(translations, "attachment.limit.reached")}
                </Alert>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Main content area */}
      <DialogContent
        sx={{
          p: 3,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: { xs: "auto", md: "hidden" }, // scroll su mobile
        }}
      >
        <Grid
          container
          spacing={3}
          sx={{
            flex: 1,
            height: { xs: "auto", md: "100%" }, // auto su mobile
            overflow: { xs: "visible", md: "hidden" }
          }}
        >
          {/* LEFT column */}
          <Grid
            item
            xs={12}
            md={7}
            sx={{
              display: "flex",
              flexDirection: "column",
              height: { xs: "auto", md: "100%" }, // auto su mobile
              overflow: { xs: "visible", md: "hidden" }
            }}
          >
            <Stack
              spacing={2}
              sx={{
                height: { xs: "auto", md: "100%" },
                overflow: { xs: "visible", md: "hidden" }
              }}
            >
              {/* Description */}
              {task?.description && (
                <Paper sx={{ p: 2, bgcolor: "white", flexShrink: 0 }} elevation={0}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, fontSize: 18 }}>
                    {getTranslation(translations, "generic.description")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {task.description}
                  </Typography>
                </Paper>
              )}

              {/* Fields - scrollable */}
              <Paper
                sx={{
                  bgcolor: "white",
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "auto",
                }}
                elevation={0}
              >
                {Object.keys(userdataEntries).length !== 0 ? (
                  <Accordion
                    elevation={0}
                    disableGutters
                    defaultExpanded
                    sx={{
                      '&:before': {
                        display: 'none',
                      },
                      boxShadow: 'none',
                      '&.MuiAccordion-root': {
                        border: 'none',
                      },
                      '&.Mui-expanded': {
                        margin: 0,
                      },
                    }}
                  >
                    <AccordionSummary expandIcon={<ArrowDropDown />}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 18 }}>
                        {getTranslation(translations, "process_model.schema.title")}
                      </Typography>
                    </AccordionSummary>

                    <AccordionDetails>
                      <FieldsList
                        canManage={doing && !readonly}
                        schema={task?.userschema}
                        selectedProcess={task?.idprocess}
                        value={userdataEntries}
                        onChange={setUserDataEntries}
                        openAddFieldDialog={openAddFieldDialog}
                        onAddFieldDialogClose={() => setOpenAddFieldDialog(false)}
                        handleSaveUserdata={async (payload) => {
                          try {
                            const { data } = await processesApi.put(`/${task?.idprocess}/userdata`, payload);
                            sendSnack({ message: data.responsemessage, type: "success" });
                            refresh && refresh();
                          } catch {
                            // ignore
                          }
                        }}
                        readonly={task?.state === 0}
                      />
                    </AccordionDetails>
                  </Accordion>
                ) : (
                  <Box sx={{ p: 2 }}>
                    <FieldsList
                      canManage={doing && !readonly}
                      schema={task?.userschema}
                      selectedProcess={task?.idprocess}
                      value={userdataEntries}
                      onChange={setUserDataEntries}
                      openAddFieldDialog={openAddFieldDialog}
                      onAddFieldDialogClose={() => setOpenAddFieldDialog(false)}
                      handleSaveUserdata={async (payload) => {
                        try {
                          const { data } = await processesApi.put(`/${task?.idprocess}/userdata`, payload);
                          sendSnack({ message: data.responsemessage, type: "success" });
                          refresh && refresh();
                        } catch {
                          // ignore
                        }
                      }}
                      readonly={task?.state === 0}
                    />
                  </Box>
                )}
                {attachments.length > 0 && (
                  <Accordion
                    elevation={0}
                    disableGutters
                    defaultExpanded
                    sx={{
                      '&:before': {
                        display: 'none',
                      },
                      boxShadow: 'none',
                      '&.MuiAccordion-root': {
                        border: 'none',
                      },
                      '&.Mui-expanded': {
                        margin: 0,
                      },
                    }}

                  >
                    <AccordionSummary expandIcon={<ArrowDropDown />}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 18 }}>
                        {getTranslation(translations, "settings.attachments.label")} ({attachments.length})
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{ mt: -3 }}
                    >
                      <AttachmentList
                        canManage={doing && !readonly}
                        attachments={attachments}
                        knownWidth={0}
                        getAttachments={getAttachments}
                        images={images}
                        histories={histories}
                      />
                    </AccordionDetails>
                  </Accordion>
                )}
              </Paper>
            </Stack>
          </Grid>

          {/* RIGHT column - Comments/History */}
          <Grid
            item
            xs={12}
            md={5}
            sx={{
              display: "flex",
              height: { xs: "auto", md: "100%" },
              minHeight: { xs: 300, md: "auto" }, // altezza minima su mobile
              overflow: { xs: "visible", md: "hidden" }
            }}
          >
            <Paper
              sx={{
                width: "100%",
                bgcolor: "#f0f0f0",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
              elevation={0}
            >
              <Box sx={{ p: 2, borderBottom: "1px solid rgba(0,0,0,0.1)", flexShrink: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 18 }}>
                  {!showHistory
                    ? getTranslation(translations, "settings.comments.label")
                    : getTranslation(translations, "settings.histories.label")
                  }
                </Typography>
              </Box>

              <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", p: 2 }}>
                {!showHistory ? (
                  <CommentsSection
                    canWrite={doing && !readonly}
                    onComment={onComment}
                    comments={comments}
                  />
                ) : (
                  <HistoriesSection
                    history={history}
                  />
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      {/* Google Drive Menu */}
      <GoogleDriveMenu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        onOpenPicker={handleOpenPicker}
        onOpenFolderPicker={handleFolderPicker}
        onCreateDocument={createAndUploadGoogleDoc}
      />

      {
        isLabelManagerShown && (
          <LabelManager
            idprocess={task?.idprocess}
            open={isLabelManagerShown}
            handleClose={handleLabelManagerClose}
          />
        )
      }
    </Dialog >
  );
};

export default TaskDetailDialog;